import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DropAlignment } from 'src/app/models/layout/drop-alignment';
import { DropSettings } from 'src/app/models/drop-settings';
import { ShelfSettings } from 'src/app/models/shelf-settings';
import { LayoutService } from 'src/app/services/layout.service';
import { PlanogramService } from 'src/app/services/planogram.service';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { CapabilityType } from 'src/app/models/capability-type';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-drop-settings-modal',
    templateUrl: './drop-settings-modal.component.html',
    styleUrls: ['./drop-settings-modal.component.scss'],
    imports: [FormsModule]
})
export class DropSettingsModalComponent implements OnInit {
  @Input() dropIndex!: number;

  startingShelfNumber: number = 1;
  height: number | null = null;
  width: number | null = null;
  depth: number | null = null;
  footerHeight: number | null = null;
  lipHeight: number | null = null;
  minSpacing: number | null = null;
  shelves: ShelfSettings[] = [];
  alignment: DropAlignment = DropAlignment.SPACE_BETWEEN;
  DropAlignment = DropAlignment;
  
  // Default values for display
  defaultHeight: number = 195;
  defaultWidth: number = 91;
  defaultDepth: number = 300;
  defaultFooterHeight: number = 20;
  defaultLipHeight: number = 4;
  defaultMinimumSpacing: number = 2;
  
  constructor(
    public activeModal: NgbActiveModal,
    public planogramService: PlanogramService,
    private layoutService: LayoutService,
    private capabilitiesService: CapabilitiesService
  ) { }

  ngOnInit(): void {
    // Get default values from planogram service
    this.defaultHeight = this.planogramService.getDropHeight();
    this.defaultWidth = this.planogramService.getShelfWidth();
    this.defaultDepth = this.planogramService.getDropDepth();
    this.defaultFooterHeight = this.planogramService.getDropFooterHeight();
    this.defaultLipHeight = this.planogramService.getShelfLipHeight();
    this.defaultMinimumSpacing = this.planogramService.getShelfMinimumSpacing();
    
    // Initialize drop settings
    let drop = this.layoutService.getDrop(this.dropIndex);
    if(!drop)
      throw new Error("Cannot edit drop setting for a drop that does not exist.");

    this.setStartShelfNumber();
    let dropSetting: DropSettings | null = this.layoutService.getDropSettings(this.dropIndex);
    
    this.alignment = dropSetting?.alignment || DropAlignment.SPACE_BETWEEN;
    this.height = dropSetting?.height ?? null;
    this.width = dropSetting?.width ?? null;
    this.depth = dropSetting?.depth ?? null;
    this.footerHeight = dropSetting?.footerHeight ?? null;
    this.lipHeight = dropSetting?.lipHeight ?? null;
    this.minSpacing = dropSetting?.minSpacing ?? null; 
    
    this.shelves = dropSetting?.shelfSettings || drop.shelves.map((shelf, index) => ({
      index: index,
      depth: shelf.shelfDepth || null,
      minHeight: shelf.minHeight || null,
      text: shelf.text || null
    }));
  }

  get canEdit(): boolean {
    return this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM);
  }

  setStartShelfNumber(){
    for(let i = 0; i < this.dropIndex; i++){
      let drop = this.layoutService.getDrop(i);
      if(drop){
        this.startingShelfNumber += drop.shelves.length;
      }
    }
  }

  onAlignmentChange(newAlignment: DropAlignment) {
    this.alignment = newAlignment;
  }

  onDropHeightChange(event: any) {
    const value = Number(event.target.value);
    this.height = !isNaN(value) && value > 0 ? value : null;
  }

  onDropWidthChange(event: any) {
    const value = Number(event.target.value);
    this.width = !isNaN(value) && value > 0 ? value : null;
  }

  onDropDepthChange(event: any) {
    const value = Number(event.target.value);
    this.depth = !isNaN(value) && value > 0 ? value : null;
  }

  onDropFooterHeightChange(event: any) {
    const value = Number(event.target.value);
    this.footerHeight = !isNaN(value) && value >= 0 ? value : null;
  }

  onDropLipHeightChange(event: any) {
    const value = Number(event.target.value);
    this.lipHeight = !isNaN(value) && value >= 0 ? value : null;
  }

  onDropMinSpacingChange(event: any) {
    const value = Number(event.target.value);
    this.minSpacing = !isNaN(value) && value >= 0 ? value : null;
  }

  clearDropHeight() {
    this.height = null;
  }

  clearDropWidth() {
    this.width = null;
  }

  clearDropDepth() {
    this.depth = null;
  }

  clearDropFooterHeight() {
    this.footerHeight = null;
  }

  clearDropLipHeight() {
    this.lipHeight = null;
  }

  clearDropMinSpacing() {
    this.minSpacing = null;
  }

  onShelfDepthChange(index: number, event: any) {
    const value = Number(event.target.value);
    this.shelves[index].depth = !isNaN(value) && value > 0 ? value : null;
  }

  onShelfMinHeightChange(index: number, event: any) {
    const value = Number(event.target.value);
    this.shelves[index].minHeight = !isNaN(value) && value >= 0 ? value : null;
  }

  clearShelfDepth(index: number) {
    this.shelves[index].depth = null;
  }

  clearShelfMinHeight(index: number) {
    this.shelves[index].minHeight = null;
  }

  onShelfTextChange(index: number, event: any) {
    this.shelves[index].text = event.target.value || null;
  }

  clearShelfText(index: number) {
    this.shelves[index].text = null;
  }

  // Helper methods to get effective values (override or default)
  getEffectiveHeight(): number {
    return this.height ?? this.defaultHeight;
  }

  getEffectiveWidth(): number {
    return this.width ?? this.defaultWidth;
  }

  getEffectiveDepth(): number {
    return this.depth ?? this.defaultDepth;
  }

  getEffectiveFooterHeight(): number {
    return this.footerHeight ?? this.defaultFooterHeight;
  }

  getEffectiveShelfDepth(index: number): number {
    return this.shelves[index].depth ?? this.getEffectiveDepth();
  }

  getEffectiveLipHeight(): number {
    return this.lipHeight ?? this.defaultLipHeight;
  }

  getEffectiveMinSpacing(): number {
    return this.minSpacing ?? this.defaultMinimumSpacing;
  }

  confirm(): void {
    const result: DropSettings = {
      index: this.dropIndex,
      height: this.height,
      width: this.width,
      depth: this.depth,
      footerHeight: this.footerHeight,
      lipHeight: this.lipHeight,
      minSpacing: this.minSpacing,  
      alignment: this.alignment,
      shelfSettings: this.shelves
    };
    this.activeModal.close(result);
  }

  cancel(): void {
    this.activeModal.close(null);
  }
}